// What size a skeleton takes while its content loads, and when it starts to
// shimmer. Plain functions, so the rules are tested without a DOM.

/** A size given by hand, for content that cannot be drawn as a shape: CSS lengths (rem), or a number of text lines. */
export interface SkeletonSize {
  width?: string;
  height?: string;
  /** Draw this many text lines (the last one shorter) instead of one block. */
  lines?: number;
}

/** The size the content had the last time it was shown, in CSS pixels. */
export interface MeasuredSize {
  width: number;
  height: number;
}

/** How long loading must last before the skeleton shows; quicker loads show nothing but the reserved space, so nothing flashes. */
export const SKELETON_DELAY_MS = 250;

/** A block's height when nothing tells the size: one line of text. */
export const DEFAULT_HEIGHT = '1rem';

/** What sizes the skeleton, in order of preference. */
export type SkeletonSource = 'shape' | 'measured' | 'given' | 'lines' | 'default';

/**
 * Picks what sizes the skeleton. A shape (the content drawn invisibly) is
 * exact; then the size the content last had; then the size given by hand;
 * then text lines; else one line's height.
 */
export function skeletonSource(hasShape: boolean, size: SkeletonSize, last: MeasuredSize | undefined): SkeletonSource {
  if (hasShape) return 'shape';
  if (last && last.height > 0) return 'measured';
  if (size.width !== undefined || size.height !== undefined) return 'given';
  if (size.lines !== undefined && size.lines > 0) return 'lines';
  return 'default';
}

/**
 * The inline size of the skeleton's box. A shape and lines size it by their
 * own layout, so they need none. A measured width is kept only inline: a
 * block fills its container, as the content did.
 */
export function skeletonStyle(source: SkeletonSource, size: SkeletonSize, last: MeasuredSize | undefined, inline: boolean): { width?: string; height?: string } {
  switch (source) {
    case 'measured':
      return { height: `${last!.height}px`, ...(inline ? { width: `${last!.width}px` } : {}) };
    case 'given':
      return { ...(size.width !== undefined ? { width: size.width } : {}), ...(size.height !== undefined ? { height: size.height } : {}) };
    case 'default':
      return { height: DEFAULT_HEIGHT };
    default:
      return {};
  }
}

/** The union of the boxes of a content's elements: the size it takes on the page. */
export function unionSize(rects: Iterable<{ left: number; top: number; right: number; bottom: number }>): MeasuredSize | undefined {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const r of rects) {
    left = Math.min(left, r.left);
    top = Math.min(top, r.top);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  }
  if (right < left || bottom < top) return undefined;
  return { width: right - left, height: bottom - top };
}
